/**
 * KidneyHealthIntelligencePage — KHI (P1)
 *
 * Обединена, образователна CKD/диализа таблица: kidney score, hero strips,
 * KDIGO стадийни лимити, дневен food-check, рисков въпросник и седмично
 * ренално меню. Надгражда Liver-Kidney Twin.
 */
import React, { useCallback, useEffect, useState } from "react";
import PhysiologyBridgeCard from "@/components/PhysiologyBridgeCard";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { twinLabel } from "@/lib/twinCatalog";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  Loader2, ArrowLeft, Activity, AlertTriangle, ShieldCheck,
  Droplets, ClipboardList, UtensilsCrossed, Info, HeartPulse,
  GitBranch, Scale, TrendingDown, Flame, Bell, Dna,
} from "lucide-react";
import { toast } from "sonner";
import KhiMyDataHub from "@/components/khi/KhiMyDataHub";
import KhiCrossTwinCard from "@/components/khi/KhiCrossTwinCard";
import KhiProtectionCard from "@/components/khi/KhiProtectionCard";
import KhiMonitoringCard from "@/components/khi/KhiMonitoringCard";
import BpDiaryCard from "@/components/khi/BpDiaryCard";
import RenalUrineCard from "@/components/khi/RenalUrineCard";
import CalciumPthCard from "@/components/shared/CalciumPthCard";
import KhiKdigoRiskCard from "@/components/khi/KhiKdigoRiskCard";
import KhiScreeningQuiz from "@/components/khi/KhiScreeningQuiz";
import KhiTodayPlanCard from "@/components/khi/KhiTodayPlanCard";
import MyDataLinkCard from "@/components/common/MyDataLinkCard";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";
import SaveToHealthIntelligenceButton from "@/components/SaveToHealthIntelligenceButton";

const TABS = [
  { id: "today", label: "Днес" },
  { id: "food", label: "Хранене" },
  { id: "monitor", label: "Проследяване" },
  { id: "why", label: "Защо е така" },
];

function Sec({ on, children }) {
  return on ? <div className="space-y-5">{children}</div> : null;
}

const BAND_COLOR = { green: "#10B981", yellow: "#84CC16", orange: "#F97316", red: "#EF4444" };

function ScoreRing({ value, band }) {
  const color = BAND_COLOR[band] || "#94A3B8";
  const r = 52, c = 2 * Math.PI * r;
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value)) / 100;
  return (
    <svg width="128" height="128" viewBox="0 0 128 128" data-testid="khi-score-ring">
      <circle cx="64" cy="64" r={r} fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth="10" />
      <circle cx="64" cy="64" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform="rotate(-90 64 64)"
        style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      <text x="64" y="60" textAnchor="middle" fontSize="30" fontWeight="800" fill="currentColor">
        {value ?? "—"}
      </text>
      <text x="64" y="80" textAnchor="middle" fontSize="12" fill="currentColor" fillOpacity="0.55">/ 100</text>
    </svg>
  );
}

function Bar({ value, max, color }) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-2 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color, transition: "width .5s ease" }} />
    </div>
  );
}

const RF_FIELDS = [
  ["diabetes", "Захарен диабет"],
  ["hypertension", "Артериална хипертония"],
  ["nsaid_use", "Редовна употреба на NSAID (обезболяващи)"],
  ["family_pkd", "Фамилна поликистозна болест (PKD)"],
  ["proteinuria_history", "Анамнеза за протеинурия"],
  ["cardiovascular_disease", "Сърдечно-съдово заболяване"],
  ["smoking", "Тютюнопушене"],
  ["aki_history", "Прекарано остро бъбречно увреждане (AKI)"],
];

export default function KidneyHealthIntelligencePage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("khi-tab") || "today"; } catch { return "today"; }
  });
  useEffect(() => {
    try { localStorage.setItem("khi-tab", tab); } catch { /* ignore */ }
  }, [tab]);

  // задача от плана за днес → отваря съответния раздел и превърта до картата
  const goTask = (t) => {
    if (t.route) { navigate(t.route); return; }
    if (t.tab) setTab(t.tab);
    if (!t.anchor) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${t.anchor}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-cyan-400");
      setTimeout(() => el.classList.remove("ring-2", "ring-cyan-400"), 1800);
    }, 220);
  };

  const [dash, setDash] = useState(null);
  const [rf, setRf] = useState({});
  const [rfSummary, setRfSummary] = useState(null);
  const [savingRf, setSavingRf] = useState(false);
  const [profForm, setProfForm] = useState({ on_dialysis: false, last_egfr: "" });
  const [savingProf, setSavingProf] = useState(false);
  const [menu, setMenu] = useState(null);
  const [menuLoading, setMenuLoading] = useState(false);
  const [pathways, setPathways] = useState(null);
  const [dialysis, setDialysis] = useState(null);
  const [dlForm, setDlForm] = useState({ pre_weight_kg: "", dry_weight_kg: "", kt_v: "", fluid_intake_ml: "", phosphate_binder_taken: false });
  const [dlSaving, setDlSaving] = useState(false);
  const [wi, setWi] = useState({ sodium: 30, protein: 20, egfr: "", acr: "" });
  const [wiResult, setWiResult] = useState(null);
  const [wiLoading, setWiLoading] = useState(false);
  const [coaching, setCoaching] = useState(null);
  const [genetics, setGenetics] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, r, pw, dl, co, ge] = await Promise.all([
        axios.get(`${API}/khi/dashboard`),
        axios.get(`${API}/khi/risk-factors`),
        axios.get(`${API}/khi/pathways`),
        axios.get(`${API}/khi/dialysis-log`),
        axios.get(`${API}/khi/coaching`),
        axios.get(`${API}/khi/genetics`),
      ]);
      setDash(d.data);
      setProfForm({
        on_dialysis: d.data?.profile?.on_dialysis || false,
        last_egfr: d.data?.egfr ?? "",
      });
      setRf(r.data?.factors || {});
      setRfSummary(r.data?.summary || null);
      setPathways(pw.data || null);
      setDialysis(dl.data || null);
      setCoaching(co.data || null);
      setGenetics(ge.data || null);
      setWi((p) => ({ ...p, egfr: d.data?.egfr ?? p.egfr, acr: d.data?.labs?.acr ?? p.acr }));
    } catch (e) {
      console.warn("KHI load failed", e);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  // чинията опреснява KHI веднага след записано хранене
  useEffect(() => {
    let timer = null;
    const onMeals = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => load(), 800);
    };
    window.addEventListener("nutramilo:meals:updated", onMeals);
    return () => {
      window.removeEventListener("nutramilo:meals:updated", onMeals);
      if (timer) clearTimeout(timer);
    };
  }, [load]);

  const saveProfile = async () => {
    setSavingProf(true);
    try {
      await axios.post(`${API}/nephro/profile`, {
        on_dialysis: !!profForm.on_dialysis,
        last_egfr: profForm.last_egfr === "" ? null : parseFloat(profForm.last_egfr),
      });
      toast.success("Профилът е записан.");
      await load();
    } catch { toast.error("Грешка при записване."); }
    finally { setSavingProf(false); }
  };

  const saveRf = async () => {
    setSavingRf(true);
    try {
      const payload = Object.fromEntries(RF_FIELDS.map(([k]) => [k, !!rf[k]]));
      const { data } = await axios.post(`${API}/khi/risk-factors`, payload);
      setRfSummary(data.summary);
      toast.success("Рисковите фактори са записани.");
    } catch { toast.error("Грешка при записване."); }
    finally { setSavingRf(false); }
  };

  const generateMenu = async () => {
    setMenuLoading(true);
    try {
      const { data } = await axios.post(`${API}/khi/weekly-menu`, {});
      setMenu(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при генериране на меню.");
    } finally { setMenuLoading(false); }
  };

  const saveDialysis = async () => {
    setDlSaving(true);
    try {
      const payload = {
        pre_weight_kg: dlForm.pre_weight_kg === "" ? null : parseFloat(dlForm.pre_weight_kg),
        dry_weight_kg: dlForm.dry_weight_kg === "" ? null : parseFloat(dlForm.dry_weight_kg),
        kt_v: dlForm.kt_v === "" ? null : parseFloat(dlForm.kt_v),
        fluid_intake_ml: dlForm.fluid_intake_ml === "" ? null : parseFloat(dlForm.fluid_intake_ml),
        phosphate_binder_taken: !!dlForm.phosphate_binder_taken,
      };
      const { data } = await axios.post(`${API}/khi/dialysis-log`, payload);
      if (data.warning_bg) toast.warning(data.warning_bg);
      else toast.success("Сесията е записана.");
      setDlForm({ pre_weight_kg: "", dry_weight_kg: "", kt_v: "", fluid_intake_ml: "", phosphate_binder_taken: false });
      const dl = await axios.get(`${API}/khi/dialysis-log`);
      setDialysis(dl.data);
    } catch { toast.error("Грешка при записване."); }
    finally { setDlSaving(false); }
  };

  const runWhatIf = async () => {
    setWiLoading(true);
    try {
      const { data } = await axios.post(`${API}/khi/what-if`, {
        sodium_reduction_pct: wi.sodium, protein_reduction_pct: wi.protein,
        egfr: wi.egfr === "" ? null : parseFloat(wi.egfr),
        acr_mg_g: wi.acr === "" ? null : parseFloat(wi.acr),
      });
      setWiResult(data);
      if (!data.available) toast.info(data.reason_bg || "Нужен е eGFR.");
    } catch { toast.error("Грешка при симулацията."); }
    finally { setWiLoading(false); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const sub = isDark ? "text-slate-400" : "text-slate-600";

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="khi-page">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1" data-testid="khi-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </button>

        <div className="flex items-center gap-2 mb-1">
          <HeartPulse className="w-6 h-6 text-cyan-500" />
          <h1 className="text-2xl font-bold">{twinLabel("kidney")}</h1>
        </div>
        <p className={`text-xs mb-4 ${sub}`}>
          Какво да направя днес, какво да ям и какво да проследявам — на прост език,
          изчислено от Вашите изследвания.
        </p>

        {!loading && (
          <div className="flex gap-1.5 overflow-x-auto pb-2 mb-4 -mx-1 px-1" data-testid="khi-tabs">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`shrink-0 px-3.5 py-2 rounded-xl text-xs font-bold transition-all ${
                  tab === t.id
                    ? "bg-gradient-to-r from-cyan-500 to-sky-600 text-white shadow-md"
                    : isDark ? "bg-white/5 hover:bg-white/10 text-slate-300"
                             : "bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"
                }`}
                data-testid={`khi-tab-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <Loader2 className="w-7 h-7 animate-spin text-cyan-500 mx-auto my-16" />
        ) : (
          <div className="space-y-5">
            <PhysiologyBridgeCard focus="kidney" isDark={isDark} />

            {/* iter1049 — урина, мехур и бъбречна безопасност (Nephro1) */}
            <RenalUrineCard isDark={isDark} />
            {/* iter1053 — калциевата ос: един източник, показан и тук, и в хормоналния */}
            <div className="mt-4">
              <CalciumPthCard isDark={isDark} />
            </div>

            {/* iter1022 — профилът „Моите данни“ захранва и KHI */}
            <ProfileContextStrip domain="kidney" isDark={isDark} />

            {/* Моите данни — единна точка за въвеждане на ренални данни */}
            <KhiMyDataHub dash={dash} genetics={genetics} onUploaded={() => load()} />

            {/* Score + hero strips */}
            <div className={`p-5 rounded-2xl border-2 ${card}`} data-testid="khi-score-card">
              <div className="flex flex-col sm:flex-row items-center gap-5">
                <ScoreRing value={dash?.score?.composite} band={dash?.score?.band} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold mb-1">Бъбречен скор</div>
                  <p className="text-sm" data-testid="khi-score-interp">{dash?.score?.interpretation_bg}</p>
                  {dash?.stage && (
                    <div className="mt-2 inline-flex items-center gap-2 text-xs font-bold px-2.5 py-1 rounded-full"
                      style={{ background: (dash?.stage_limits?.color || "#94A3B8") + "22", color: dash?.stage_limits?.color }}
                      data-testid="khi-stage-badge">
                      <Activity className="w-3.5 h-3.5" />
                      {dash?.stage_limits?.label_bg || dash?.stage}
                    </div>
                  )}
                </div>
              </div>

              {dash?.hero_strips?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4" data-testid="khi-hero-strips">
                  {dash.hero_strips.map((s) => (
                    <div key={s.id} className={`p-2.5 rounded-xl border ${isDark ? "border-white/10 bg-white/[0.03]" : "border-gray-200 bg-slate-50"}`}
                      data-testid={`khi-strip-${s.id}`}>
                      <div className="text-[10px] uppercase tracking-wide opacity-60">{s.label_bg}</div>
                      <div className="text-sm font-bold truncate" style={{ color: s.color }}>{s.value_bg}</div>
                      {s.sub_bg && <div className="text-[10px] opacity-55 truncate">{s.sub_bg}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* iter1022 — снимката влиза в Health Intelligence */}
              <div className="mt-4">
                <SaveToHealthIntelligenceButton
                  snapshotType="digital_twin"
                  title={`Бъбречно здраве — ${dash?.score?.composite ?? "—"}/100`}
                  summary={`Бъбречен скор ${dash?.score?.composite ?? "—"}/100`
                    + (dash?.stage ? ` · стадий ${dash.stage}` : "")
                    + (dash?.score?.band ? ` · ${dash.score.band}` : "")}
                  payload={{
                    source: "kidney_health_intelligence", module: "kidney",
                    score: dash?.score, stage: dash?.stage,
                    stage_limits: dash?.stage_limits, hero_strips: dash?.hero_strips,
                  }}
                  tags={["kidney", "khi", dash?.score?.band].filter(Boolean)}
                  testId="khi-save-to-hi"
                />
              </div>
            </div>

            {/* === Днес === */}
            <Sec on={tab === "today"}>
              <KhiTodayPlanCard cardCls={card} isDark={isDark} onGo={goTask} />
            </Sec>

            {/* CKD coaching — streak, nudges, weekly digest */}
            <Sec on={tab === "today"}>
            {coaching && (
              <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-coaching-card">
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-cyan-500" />
                    <h2 className="text-sm font-bold">Ежедневен коучинг</h2>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm font-bold px-2.5 py-1 rounded-full bg-orange-500/15 text-orange-500"
                    data-testid="khi-streak">
                    <Flame className="w-4 h-4" /> {coaching.streak_days} дни серия
                  </div>
                </div>
                <ul className="space-y-2" data-testid="khi-nudges">
                  {coaching.nudges.map((n) => {
                    const col = n.severity === "high" ? "#EF4444" : n.severity === "medium" ? "#F59E0B" : "#10B981";
                    return (
                      <li key={n.id} className="flex items-start gap-2 text-[12px]" data-testid={`khi-nudge-${n.id}`}>
                        <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: col }} />
                        <span>{n.text_bg}</span>
                      </li>
                    );
                  })}
                </ul>
                {coaching.weekly_digest && (
                  <div className={`mt-3 p-2.5 rounded-xl text-[11px] ${isDark ? "bg-white/[0.03]" : "bg-slate-50"}`} data-testid="khi-weekly-digest">
                    <div className="font-bold mb-0.5">Седмичен дайджест</div>
                    <div className="opacity-75">{coaching.weekly_digest.summary_bg}</div>
                    {coaching.weekly_digest.avg_potassium_mg != null && (
                      <div className="opacity-60 mt-0.5">
                        Ср. калий: {coaching.weekly_digest.avg_potassium_mg} mg · диализни сесии: {coaching.weekly_digest.dialysis_sessions}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            </Sec>

            {/* Nephro profile mini-form */}
            <Sec on={tab === "monitor"}>
            <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-profile-card">
              <div className="flex items-center gap-2 mb-3">
                <Droplets className="w-4 h-4 text-cyan-500" />
                <h2 className="text-sm font-bold">Моят бъбречен статус</h2>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="text-[11px] opacity-70 block mb-1">eGFR (mL/min/1.73m²)</label>
                  <input type="number" value={profForm.last_egfr} data-testid="khi-egfr-input"
                    onChange={(e) => setProfForm((p) => ({ ...p, last_egfr: e.target.value }))}
                    className={`w-32 px-2.5 py-1.5 rounded-lg border text-sm ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`} />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="khi-dialysis-toggle">
                  <input type="checkbox" checked={!!profForm.on_dialysis}
                    onChange={(e) => setProfForm((p) => ({ ...p, on_dialysis: e.target.checked }))} />
                  На диализа
                </label>
                <Button size="sm" onClick={saveProfile} disabled={savingProf} data-testid="khi-save-profile-btn">
                  {savingProf ? <Loader2 className="w-4 h-4 animate-spin" /> : "Запази"}
                </Button>
              </div>
            </div>
            </Sec>

            {/* Stage limits */}
            <Sec on={tab === "food"}>
            {dash?.stage_limits && (
              <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-limits-card">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-4 h-4 text-cyan-500" />
                  <h2 className="text-sm font-bold">Дневни KDIGO лимити за вашия стадий</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  <Limit label="Протеин" v={dash.stage_limits.protein?.label_bg} />
                  <Limit label="Натрий" v={dash.stage_limits.sodium_mg?.label_bg} />
                  <Limit label="Калий" v={dash.stage_limits.potassium_mg?.label_bg} />
                  <Limit label="Фосфор" v={dash.stage_limits.phosphorus_mg?.label_bg} />
                  <Limit label="Енергия" v={dash.stage_limits.energy_kcal_kg?.label_bg} />
                  <Limit label="Течности" v={dash.stage_limits.fluid?.label_bg} />
                </div>
              </div>
            )}

            {/* Бърза връзка към симулатора на чинията */}
            <button onClick={() => navigate("/dashboard")}
              className={`w-full p-4 rounded-2xl border text-left hover:shadow-md transition-shadow ${card}`}
              data-testid="khi-open-plate-btn">
              <div className="flex items-center gap-3">
                <UtensilsCrossed className="w-5 h-5 text-cyan-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">Отвори симулатора на чинията</div>
                  <div className={`text-[11px] mt-0.5 ${sub}`}>
                    Там записвате храната и веднага виждате калия, фосфора, солта и белтъка
                    спрямо лимитите за Вашия стадий — със замяна на храна с едно докосване.
                  </div>
                </div>
              </div>
            </button>
            </Sec>

            {/* Food-check today */}
            <Sec on={tab === "today"}>
            {dash?.food_today?.active && (
              <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-foodcheck-card">
                <div className="flex items-center gap-2 mb-1">
                  <ClipboardList className="w-4 h-4 text-cyan-500" />
                  <h2 className="text-sm font-bold">Днешен прием vs лимит</h2>
                </div>
                <p className={`text-[11px] mb-3 ${sub}`}>
                  От {dash.food_today.meals_logged} записани храни, {dash.food_today.meals_with_mineral_data} имат минерални данни.
                </p>
                <div className="space-y-3">
                  {[["potassium_mg", "Калий"], ["phosphorus_mg", "Фосфор"], ["sodium_mg", "Натрий"]].map(([k, label]) => {
                    const t = dash.food_today.totals[k] || 0;
                    const max = dash.food_today.ceilings[k]?.max;
                    const over = max && t > max;
                    return (
                      <div key={k} data-testid={`khi-foodcheck-${k}`}>
                        <div className="flex justify-between text-[11px] mb-1">
                          <span className="font-semibold">{label}</span>
                          <span className={over ? "text-red-500 font-bold" : "opacity-70"}>{t} / {max ?? "—"} mg</span>
                        </div>
                        <Bar value={t} max={max} color={over ? "#EF4444" : "#10B981"} />
                      </div>
                    );
                  })}
                </div>
                {dash.food_today.breaches?.length > 0 && (
                  <div className="mt-3 p-2 rounded-md bg-red-500/10 text-red-500 text-[11px] flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    Днес сте над лимита за: {dash.food_today.breaches.join(", ")}. Обсъдете с нефролог.
                  </div>
                )}
              </div>
            )}
            </Sec>

            {/* Renal pathway engine */}
            <Sec on={tab === "why"}>
            {pathways?.pathways?.length > 0 && (
              <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-pathways-card">
                <div className="flex items-center gap-2 mb-1">
                  <GitBranch className="w-4 h-4 text-cyan-500" />
                  <h2 className="text-sm font-bold">Ренални физиологични пътища</h2>
                </div>
                <p className={`text-[11px] mb-3 ${sub}`}>
                  Обяснимост като при BHI — кои CKD механизми са активни според вашите данни. {pathways.active_count} активни.
                </p>
                <div className="space-y-2">
                  {pathways.pathways.map((p) => {
                    const col = { green: "#10B981", yellow: "#84CC16", orange: "#F97316", red: "#EF4444", unknown: "#94A3B8" }[p.status] || "#94A3B8";
                    return (
                      <div key={p.id} className={`p-2.5 rounded-xl border ${isDark ? "border-white/10 bg-white/[0.03]" : "border-gray-200 bg-slate-50"}`}
                        data-testid={`khi-pathway-${p.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[12px] font-bold">{p.name_bg}</span>
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                            style={{ background: col + "22", color: col }}>
                            {p.status === "unknown" ? "няма данни" : p.status}
                          </span>
                        </div>
                        <div className="text-[10px] opacity-70 mt-0.5">{p.summary_bg}</div>
                        <div className="text-[10px] opacity-60 mt-1">→ {p.reasons_bg?.[0]}</div>
                        {p.dietary_levers_bg?.length > 0 && (
                          <div className="text-[10px] mt-1">
                            <span className="opacity-60">Хранителни лостове: </span>
                            <span className="opacity-80">{p.dietary_levers_bg.join(" · ")}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </Sec>

            {/* Стадий и риск по KDIGO + въпросник за изследване */}
            <Sec on={tab === "monitor"}>
              <KhiKdigoRiskCard cardCls={card} isDark={isDark} />
              <KhiScreeningQuiz cardCls={card} isDark={isDark} />

              {/* Защита на бъбрека и график за проследяване */}
              <KhiProtectionCard cardCls={card} isDark={isDark} />
              <KhiMonitoringCard cardCls={card} />
              <BpDiaryCard cardCls={card} isDark={isDark} />
            </Sec>

            {/* Cross-Twin: бъбреци ↔ метаболизъм / черен дроб / кардио */}
            <Sec on={tab === "why"}>
              <KhiCrossTwinCard />
            </Sec>

            {/* Risk-factor questionnaire */}
            <Sec on={tab === "why"}>
            <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-riskfactors-card">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <h2 className="text-sm font-bold">Рискови фактори за ХБН</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {RF_FIELDS.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`khi-rf-${k}`}>
                    <input type="checkbox" checked={!!rf[k]}
                      onChange={(e) => setRf((p) => ({ ...p, [k]: e.target.checked }))} />
                    {label}
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
                {rfSummary && (
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    rfSummary.level === "high" ? "bg-red-500/20 text-red-500"
                    : rfSummary.level === "moderate" ? "bg-amber-500/20 text-amber-500"
                    : "bg-emerald-500/20 text-emerald-500"}`} data-testid="khi-rf-summary">
                    {rfSummary.note_bg}
                  </span>
                )}
                <Button size="sm" onClick={saveRf} disabled={savingRf} data-testid="khi-save-rf-btn">
                  {savingRf ? <Loader2 className="w-4 h-4 animate-spin" /> : "Запази"}
                </Button>
              </div>
            </div>
            </Sec>

            {/* Renal weekly menu */}
            <Sec on={tab === "food"}>
            <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-menu-card">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <UtensilsCrossed className="w-4 h-4 text-cyan-500" />
                  <h2 className="text-sm font-bold">Седмично ренално меню</h2>
                </div>
                <Button size="sm" onClick={generateMenu} disabled={menuLoading} data-testid="khi-generate-menu-btn">
                  {menuLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Генерирай"}
                </Button>
              </div>
              {menu && (
                <div data-testid="khi-menu-result">
                  <div className={`text-[11px] mb-3 ${sub}`}>
                    Стадий <b>{menu.stage}</b> · лимити: K ≤ {menu.ceilings.potassium_mg_max} · P ≤ {menu.ceilings.phosphorus_mg_max} ·
                    Na ≤ {menu.ceilings.sodium_mg_max} mg · протеин ~{menu.ceilings.protein_g_day} g/ден · {menu.ceilings.fluid_bg}
                  </div>
                  <div className="space-y-2">
                    {menu.days.map((d) => {
                      const ok = Object.values(d.within_limits).every(Boolean);
                      return (
                        <div key={d.day} className={`p-2.5 rounded-xl border ${isDark ? "border-white/10 bg-white/[0.03]" : "border-gray-200 bg-slate-50"}`}
                          data-testid={`khi-menu-day-${d.day}`}>
                          <div className="flex items-center justify-between text-[11px] font-bold mb-1">
                            <span>Ден {d.day}</span>
                            <span className={ok ? "text-emerald-500" : "text-amber-500"}>
                              K {d.totals.potassium_mg} · P {d.totals.phosphorus_mg} · Na {d.totals.sodium_mg} mg
                              {ok ? " ✓" : " ⚠"}
                            </span>
                          </div>
                          <div className="text-[11px] opacity-75 space-y-0.5">
                            {Object.entries(d.meals).map(([mt, m]) => (
                              <div key={mt}>· {m.name_bg}</div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {menu.warnings?.length > 0 && (
                    <div className="mt-2 text-[11px] text-amber-500">{menu.warnings.join(" ")}</div>
                  )}
                  <p className="text-[10px] opacity-55 mt-3 leading-relaxed">{menu.disclaimer_bg}</p>
                </div>
              )}
            </div>
            </Sec>

            {/* Kidney What-If simulator */}
            <Sec on={tab === "why"}>
            <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-whatif-card">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-4 h-4 text-cyan-500" />
                <h2 className="text-sm font-bold">What-If: намаляване на натрий/протеин</h2>
              </div>
              <p className={`text-[11px] mb-3 ${sub}`}>Илюстративна проекция как намаляването влияе на eGFR траекторията и KFRE риска.</p>
              <div className="flex flex-wrap gap-3 mb-3">
                <div>
                  <label className="text-[10px] opacity-70 block mb-1">eGFR</label>
                  <input type="number" value={wi.egfr} data-testid="khi-wi-egfr"
                    onChange={(e) => setWi((p) => ({ ...p, egfr: e.target.value }))}
                    className={`w-24 px-2 py-1.5 rounded-lg border text-sm ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`} />
                </div>
                <div>
                  <label className="text-[10px] opacity-70 block mb-1">UACR (mg/g)</label>
                  <input type="number" value={wi.acr} data-testid="khi-wi-acr"
                    onChange={(e) => setWi((p) => ({ ...p, acr: e.target.value }))}
                    className={`w-24 px-2 py-1.5 rounded-lg border text-sm ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`} />
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-[11px] mb-1"><span>Намаляване на натрий</span><span className="font-bold">{wi.sodium}%</span></div>
                  <input type="range" min="0" max="100" step="5" value={wi.sodium} data-testid="khi-wi-sodium"
                    onChange={(e) => setWi((p) => ({ ...p, sodium: parseInt(e.target.value) }))} className="w-full" />
                </div>
                <div>
                  <div className="flex justify-between text-[11px] mb-1"><span>Намаляване на протеин</span><span className="font-bold">{wi.protein}%</span></div>
                  <input type="range" min="0" max="100" step="5" value={wi.protein} data-testid="khi-wi-protein"
                    onChange={(e) => setWi((p) => ({ ...p, protein: parseInt(e.target.value) }))} className="w-full" />
                </div>
                <Button size="sm" onClick={runWhatIf} disabled={wiLoading} data-testid="khi-wi-run-btn">
                  {wiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Симулирай"}
                </Button>
              </div>
              {wiResult?.available && (
                <div className="mt-4" data-testid="khi-wi-result">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className={`p-2.5 rounded-xl ${isDark ? "bg-white/[0.03]" : "bg-slate-50"}`}>
                      <div className="text-[10px] opacity-60">По-бавен спад на eGFR</div>
                      <div className="text-lg font-bold text-emerald-500">−{wiResult.decline_reduction_pct}%</div>
                    </div>
                    <div className={`p-2.5 rounded-xl ${isDark ? "bg-white/[0.03]" : "bg-slate-50"}`}>
                      <div className="text-[10px] opacity-60">Намаление на протеинурия</div>
                      <div className="text-lg font-bold text-emerald-500">−{wiResult.acr_reduction_pct}%</div>
                    </div>
                  </div>
                  {/* eGFR trajectory mini-chart */}
                  <div className="mt-3">
                    <div className="text-[11px] font-semibold mb-1">Прогнозна eGFR траектория ({wiResult.years[wiResult.years.length - 1]} г.)</div>
                    <div className="flex items-end gap-1 h-24">
                      {wiResult.years.map((y, i) => {
                        const base = wiResult.egfr_trajectory_baseline[i];
                        const interv = wiResult.egfr_trajectory_intervention[i];
                        const maxV = wiResult.egfr_trajectory_baseline[0] || 100;
                        return (
                          <div key={y} className="flex-1 flex flex-col items-center justify-end gap-0.5">
                            <div className="w-full flex gap-0.5 items-end" style={{ height: "80px" }}>
                              <div className="flex-1 rounded-t bg-slate-400/50" style={{ height: `${(base / maxV) * 100}%` }} title={`Без промяна: ${base}`} />
                              <div className="flex-1 rounded-t bg-cyan-500" style={{ height: `${(interv / maxV) * 100}%` }} title={`С промяна: ${interv}`} />
                            </div>
                            <span className="text-[8px] opacity-50">{y}г</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-3 text-[9px] opacity-60 mt-1">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-400/50 inline-block" /> Без промяна</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-cyan-500 inline-block" /> С промяна</span>
                    </div>
                  </div>
                  {wiResult.kfre_baseline && wiResult.kfre_intervention && (
                    <div className="mt-3 text-[11px]">
                      <div className="font-semibold mb-1">KFRE риск от бъбречна недостатъчност (при намалена протеинурия)</div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>2 г.: <b>{wiResult.kfre_baseline.risk_2yr_pct}%</b> → <b className="text-emerald-500">{wiResult.kfre_intervention.risk_2yr_pct}%</b></div>
                        <div>5 г.: <b>{wiResult.kfre_baseline.risk_5yr_pct}%</b> → <b className="text-emerald-500">{wiResult.kfre_intervention.risk_5yr_pct}%</b></div>
                      </div>
                    </div>
                  )}
                  <p className="text-[10px] opacity-55 mt-3 leading-relaxed">{wiResult.disclaimer_bg}</p>
                </div>
              )}
            </div>
            </Sec>

            {/* Dialysis tracking (shown when on dialysis) */}
            <Sec on={tab === "monitor"}>
            {(dash?.profile?.on_dialysis || dialysis?.summary?.sessions > 0) && (
              <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-dialysis-card">
                <div className="flex items-center gap-2 mb-1">
                  <Scale className="w-4 h-4 text-cyan-500" />
                  <h2 className="text-sm font-bold">Диализен дневник</h2>
                </div>
                <p className={`text-[11px] mb-3 ${sub}`}>IDWG, Kt/V, течности и фосфат-свързватели. Цели: IDWG &lt; 3–5% · Kt/V ≥ 1.2.</p>
                {dialysis?.summary?.sessions > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3" data-testid="khi-dialysis-summary">
                    <SummaryTile label="Ср. IDWG" v={dialysis.summary.avg_idwg_pct != null ? `${dialysis.summary.avg_idwg_pct}%` : "—"} isDark={isDark} />
                    <SummaryTile label="Ср. Kt/V" v={dialysis.summary.avg_kt_v ?? "—"} isDark={isDark} />
                    <SummaryTile label="Свързватели" v={dialysis.summary.binder_adherence_pct != null ? `${dialysis.summary.binder_adherence_pct}%` : "—"} isDark={isDark} />
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <DlInput label="Тегло преди (kg)" v={dlForm.pre_weight_kg} onChange={(x) => setDlForm((p) => ({ ...p, pre_weight_kg: x }))} isDark={isDark} testid="khi-dl-pre" />
                  <DlInput label="Сухо тегло (kg)" v={dlForm.dry_weight_kg} onChange={(x) => setDlForm((p) => ({ ...p, dry_weight_kg: x }))} isDark={isDark} testid="khi-dl-dry" />
                  <DlInput label="Kt/V" v={dlForm.kt_v} onChange={(x) => setDlForm((p) => ({ ...p, kt_v: x }))} isDark={isDark} testid="khi-dl-ktv" />
                  <DlInput label="Течности (ml)" v={dlForm.fluid_intake_ml} onChange={(x) => setDlForm((p) => ({ ...p, fluid_intake_ml: x }))} isDark={isDark} testid="khi-dl-fluid" />
                </div>
                <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="khi-dl-binder">
                    <input type="checkbox" checked={!!dlForm.phosphate_binder_taken}
                      onChange={(e) => setDlForm((p) => ({ ...p, phosphate_binder_taken: e.target.checked }))} />
                    Взех фосфат-свързватели
                  </label>
                  <Button size="sm" onClick={saveDialysis} disabled={dlSaving} data-testid="khi-dl-save-btn">
                    {dlSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Запиши сесия"}
                  </Button>
                </div>
                {dialysis?.entries?.length > 0 && (
                  <div className="mt-3 space-y-1" data-testid="khi-dialysis-entries">
                    {dialysis.entries.slice(0, 5).map((e, i) => (
                      <div key={i} className="text-[11px] flex justify-between opacity-75">
                        <span>{e.session_date}</span>
                        <span>IDWG {e.idwg_pct ?? "—"}% · Kt/V {e.kt_v ?? "—"} · {e.phosphate_binder_taken ? "свързватели ✓" : "без ✗"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            </Sec>

            {/* Renal genetics */}
            <Sec on={tab === "why"}>
            {genetics?.genes?.length > 0 && (
              <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-genetics-card">
                <div className="flex items-center gap-2 mb-1">
                  <Dna className="w-4 h-4 text-cyan-500" />
                  <h2 className="text-sm font-bold">Наследствен бъбречен контекст</h2>
                </div>
                <p className={`text-[11px] mb-3 ${sub}`}>{genetics.counseling_note_bg}</p>
                <div className="space-y-2">
                  {genetics.genes.map((g) => (
                    <div key={g.id} className={`p-2.5 rounded-xl border ${g.highlighted ? "border-cyan-400 bg-cyan-500/10" : isDark ? "border-white/10 bg-white/[0.03]" : "border-gray-200 bg-slate-50"}`}
                      data-testid={`khi-gene-${g.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[12px] font-bold">{g.name_bg}</span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-500/20 opacity-80">{g.inheritance_bg}</span>
                        {g.highlighted && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/25 text-cyan-500 font-bold">свързано с вашата фамилна анамнеза</span>}
                      </div>
                      <div className="text-[11px] opacity-75 mt-1">{g.summary_bg}</div>
                      <div className="text-[10px] opacity-60 mt-1">🩺 {g.counseling_bg}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Declared etiologies */}
            {dash?.declared_etiologies?.length > 0 && (
              <div className={`p-4 rounded-2xl border ${card}`} data-testid="khi-etiologies-card">
                <h2 className="text-sm font-bold mb-2">Вашите декларирани бъбречни диагнози</h2>
                {dash.declared_etiologies.map((e) => (
                  <div key={e.id} className="mb-2">
                    <div className="text-sm font-semibold">{e.name_bg}</div>
                    <div className="text-[11px] opacity-70">{e.education_bg}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Автодиагнозата живее в профила — тук само връзка, без дублиране */}
            <MyDataLinkCard
              testid="khi-autodx-link"
              title="Автодиагноза от всички Ваши изследвания"
              note="Общата автодиагноза (бъбреци, метаболизъм, хормони, възпаление) се
                    изчислява веднъж от едните и същи данни и живее в профила."
              cta="Виж автодиагнозата в раздела Моите данни" />
            </Sec>

            {/* Disclaimer */}
            <div className={`p-3 rounded-xl text-[11px] flex items-start gap-2 ${isDark ? "bg-amber-500/10 text-amber-300" : "bg-amber-50 text-amber-800"}`}>
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{dash?.disclaimer_bg}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Limit({ label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide opacity-55">{label}</div>
      <div className="text-[12px] font-medium leading-snug">{v || "—"}</div>
    </div>
  );
}

function SummaryTile({ label, v, isDark }) {
  return (
    <div className={`p-2 rounded-xl text-center ${isDark ? "bg-white/[0.03]" : "bg-slate-50"}`}>
      <div className="text-[9px] uppercase opacity-55">{label}</div>
      <div className="text-sm font-bold text-cyan-500">{v}</div>
    </div>
  );
}

function DlInput({ label, v, onChange, isDark, testid }) {
  return (
    <div>
      <label className="text-[10px] opacity-70 block mb-1">{label}</label>
      <input type="number" value={v} data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-2 py-1.5 rounded-lg border text-sm ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`} />
    </div>
  );
}
